import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getValidToken, API_URL } from '../../utils/authUtils';
import AdminRouteGuard from '../AdminRouteGuard';

const BACKEND_URL = API_URL;

const STAGES = [
  { value: 'researching', label: 'Researching', color: 'bg-gray-100 text-gray-800' },
  { value: 'loi_drafting', label: 'LOI Drafting', color: 'bg-blue-100 text-blue-800' },
  { value: 'loi_submitted', label: 'LOI Submitted', color: 'bg-blue-200 text-blue-900' },
  { value: 'invited_to_apply', label: 'Invited to Apply', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'proposal_drafting', label: 'Proposal Drafting', color: 'bg-purple-100 text-purple-800' },
  { value: 'submitted', label: 'Submitted', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'under_review', label: 'Under Review', color: 'bg-orange-100 text-orange-800' },
  { value: 'awarded', label: 'Awarded', color: 'bg-green-100 text-green-800' },
  { value: 'declined', label: 'Declined', color: 'bg-red-100 text-red-800' },
  { value: 'deferred', label: 'Deferred', color: 'bg-amber-100 text-amber-800' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'text-gray-500' },
  { value: 'medium', label: 'Medium', color: 'text-amber-600' },
  { value: 'high', label: 'High', color: 'text-orange-600' },
  { value: 'urgent', label: 'Urgent', color: 'text-red-600' },
];

const FUNDER_TYPES = [
  { value: 'foundation', label: 'Foundation' },
  { value: 'government', label: 'Government' },
  { value: 'corporate', label: 'Corporate' },
  { value: 'other', label: 'Other' },
];


const STUDENT_LEVELS = [
  { value: 'College', label: 'College' },
  { value: 'Professional', label: 'Professional' },
  { value: 'College + Professional', label: 'College + Professional' },
  { value: 'High School', label: 'High School' },
  { value: 'High School + College', label: 'High School + College' },
  { value: 'College + Bridge', label: 'College + Bridge' },
  { value: 'Bridge', label: 'Bridge' },
];

const LOI_OR_PROPOSAL = [
  { value: 'LOI', label: 'LOI' },
  { value: 'Full Proposal', label: 'Full Proposal' },
  { value: 'LOI + Full Proposal', label: 'LOI + Full Proposal' },
  { value: 'Invite Only', label: 'Invite Only' },
  { value: 'Application', label: 'Application' },
];

const DOC_CATEGORIES = [
  { value: 'financials', label: 'Financials', icon: '💰' },
  { value: 'mission_statement', label: 'Mission Statement', icon: '🎯' },
  { value: 'program_description', label: 'Program Description', icon: '📋' },
  { value: 'boilerplate', label: 'Boilerplate', icon: '📝' },
  { value: 'budget_template', label: 'Budget Template', icon: '📊' },
  { value: 'prior_proposal', label: 'Prior Proposal', icon: '📄' },
  { value: 'supporting_data', label: 'Supporting Data', icon: '📈' },
  { value: 'other', label: 'Other', icon: '📎' },
];

const AdminGrantManagement = () => {
  const navigate = useNavigate();

  // Access control
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessChecked, setAccessChecked] = useState(false);

  // Data state
  const [opportunities, setOpportunities] = useState([]);
  const [pipelineStats, setPipelineStats] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Pagination & filters
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [stageFilter, setStageFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [sortBy, setSortBy] = useState('deadline');
  const [sortDir, setSortDir] = useState('asc');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [importLoading, setImportLoading] = useState(false);

  // Quick add
  const [quickAdd, setQuickAdd] = useState({ title: '', funder_name: '', deadline: '', stage: 'researching' });
  const [quickAddLoading, setQuickAddLoading] = useState(false);

  // Create form
  const [createForm, setCreateForm] = useState({
    title: '', funder_name: '', funder_type: '', grant_url: '', description: '',
    amount_min: '', amount_max: '', deadline: '', deadline_type: '', deadline_notes: '',
    student_level: '', loi_or_proposal: '', next_step_required: '', notes: '', probability_of_win: '',
    funder_contact_name: '', funder_contact_email: '', funder_contact_phone: '', funder_contact_linkedin: '',
    stage: 'researching', priority: 'medium',
    focus_areas: '', eligibility_notes: '', funder_requirements: '', internal_notes: '', assigned_to: '',
  });
  const [createLoading, setCreateLoading] = useState(false);

  // Main tab
  const [activeMainTab, setActiveMainTab] = useState('pipeline');

  // Detail slide-out
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [detailTab, setDetailTab] = useState('overview');
  const [editForm, setEditForm] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Documents (Phase 2a)
  const [documents, setDocuments] = useState([]);
  const [docLoading, setDocLoading] = useState(false);
  const [docTotal, setDocTotal] = useState(0);
  const [docPage, setDocPage] = useState(1);
  const [docTotalPages, setDocTotalPages] = useState(1);
  const [docCategory, setDocCategory] = useState('');
  const [docSearch, setDocSearch] = useState('');
  const [docTag, setDocTag] = useState('');
  const [allTags, setAllTags] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadForm, setUploadForm] = useState({ title: '', category: 'other', description: '', tags: '' });
  const [uploadLoading, setUploadLoading] = useState(false);
  const [showDocDetail, setShowDocDetail] = useState(null);
  const [docDetailLoading, setDocDetailLoading] = useState(false);
  const [editDocForm, setEditDocForm] = useState(null);

  // Proposals (Phase 2b)
  const [oppProposals, setOppProposals] = useState([]);
  const [oppProposalsLoading, setOppProposalsLoading] = useState(false);
  const [activeProposal, setActiveProposal] = useState(null); // full-screen editor
  const [proposalContent, setProposalContent] = useState('');
  const [proposalTitle, setProposalTitle] = useState('');
  const [proposalSaving, setProposalSaving] = useState(false);
  const [proposalVersions, setProposalVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [showNewProposalForm, setShowNewProposalForm] = useState(false);
  const [newProposalForm, setNewProposalForm] = useState({ title: '', proposal_type: 'full_proposal' });
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // AI Assist (Phase 3)
  const [showAIGenerateModal, setShowAIGenerateModal] = useState(false);
  const [aiInstructions, setAiInstructions] = useState('');
  const [aiContextDocIds, setAiContextDocIds] = useState([]);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [showExtractReqModal, setShowExtractReqModal] = useState(false);
  const [extractDocId, setExtractDocId] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [extractedReqs, setExtractedReqs] = useState(null);
  // Cost tracking
  const [bedrockUsage, setBedrockUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageDateRange, setUsageDateRange] = useState({ start: '', end: '' });
  const [costsTabVisible, setCostsTabVisible] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = getValidToken();
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, []);

  // Check access permission
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const token = getValidToken();
        if (!token) { setAccessDenied(true); setAccessChecked(true); return; }
        const res = await fetch(`${BACKEND_URL}/api/admin/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Access check failed');
        const data = await res.json();
        if (data?.is_super_admin) setIsSuperAdmin(true);
        if (data?.hide_grant_management === true && !data?.is_super_admin) {
          setAccessDenied(true);
        }
      } catch (err) {
        try {
          const token = localStorage.getItem('token');
          if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]));
            if (payload.role === 'admin' && payload.is_super_admin) {
              setAccessChecked(true);
              return;
            }
          }
        } catch {}
        setAccessDenied(true);
      } finally {
        setAccessChecked(true);
      }
    };
    checkAccess();
  }, []);

  // Fetch opportunities
  const fetchOpportunities = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '50',
        is_archived: showArchived.toString(),
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      if (stageFilter) params.set('stage', stageFilter);
      if (priorityFilter) params.set('priority', priorityFilter);
      if (assigneeFilter) params.set('assigned_to', assigneeFilter);
      if (searchTerm) params.set('search', searchTerm);

      const res = await fetch(`${BACKEND_URL}/api/admin/grants/opportunities?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to load opportunities');
      const data = await res.json();
      setOpportunities(data.opportunities);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, stageFilter, priorityFilter, assigneeFilter, searchTerm, showArchived, sortBy, sortDir, getAuthHeaders]);

  // Fetch pipeline stats
  const fetchPipelineStats = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/pipeline-stats`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setPipelineStats(data);
      }
    } catch (err) {
      console.error('Failed to load pipeline stats:', err);
    }
  }, [getAuthHeaders]);

  // Fetch staff list
  const fetchStaff = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/staff`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setStaff(data.staff);
      }
    } catch (err) {
      console.error('Failed to load staff:', err);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (accessChecked && !accessDenied) fetchOpportunities();
  }, [fetchOpportunities, accessChecked, accessDenied]);

  useEffect(() => {
    if (accessChecked && !accessDenied) {
      fetchPipelineStats();
      fetchStaff();
    }
  }, [fetchPipelineStats, fetchStaff, accessChecked, accessDenied]);

  // Clear messages after delay
  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 6000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Quick Add
  const handleQuickAdd = async () => {
    if (!quickAdd.title.trim() || !quickAdd.funder_name.trim()) {
      setError('Title and funder name are required');
      return;
    }
    setQuickAddLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/opportunities`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          title: quickAdd.title.trim(),
          funder_name: quickAdd.funder_name.trim(),
          deadline: quickAdd.deadline || null,
          stage: quickAdd.stage,
        }),
      });
      if (!res.ok) throw new Error('Failed to create opportunity');
      setQuickAdd({ title: '', funder_name: '', deadline: '', stage: 'researching' });
      setSuccessMsg('Opportunity created');
      fetchOpportunities();
      fetchPipelineStats();
    } catch (err) {
      setError(err.message);
    } finally {
      setQuickAddLoading(false);
    }
  };

  // Full Create
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.title.trim() || !createForm.funder_name.trim()) {
      setError('Title and funder name are required');
      return;
    }
    setCreateLoading(true);
    try {
      const payload = {
        ...createForm,
        amount_min: createForm.amount_min ? parseFloat(createForm.amount_min) : null,
        amount_max: createForm.amount_max ? parseFloat(createForm.amount_max) : null,
        deadline: createForm.deadline || null,
        deadline_type: createForm.deadline_type || null,
        deadline_notes: createForm.deadline_notes || null,
        student_level: createForm.student_level || null,
        loi_or_proposal: createForm.loi_or_proposal || null,
        next_step_required: createForm.next_step_required || null,
        notes: createForm.notes || null,
        probability_of_win: createForm.probability_of_win !== '' ? parseInt(createForm.probability_of_win) : null,
        funder_contact_name: createForm.funder_contact_name || null,
        funder_contact_email: createForm.funder_contact_email || null,
        funder_contact_phone: createForm.funder_contact_phone || null,
        funder_contact_linkedin: createForm.funder_contact_linkedin || null,
        assigned_to: createForm.assigned_to ? parseInt(createForm.assigned_to) : null,
        focus_areas: createForm.focus_areas ? createForm.focus_areas.split(';').map(s => s.trim()).filter(Boolean) : [],
      };
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/opportunities`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed to create opportunity');
      setShowCreateModal(false);
      setCreateForm({
        title: '', funder_name: '', funder_type: '', grant_url: '', description: '',
        amount_min: '', amount_max: '', deadline: '', deadline_type: '', deadline_notes: '',
        student_level: '', loi_or_proposal: '', next_step_required: '', notes: '', probability_of_win: '',
        funder_contact_name: '', funder_contact_email: '', funder_contact_phone: '', funder_contact_linkedin: '',
        stage: 'researching', priority: 'medium',
        focus_areas: '', eligibility_notes: '', funder_requirements: '', internal_notes: '', assigned_to: '',
      });
      setSuccessMsg('Opportunity created successfully');
      fetchOpportunities();
      fetchPipelineStats();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreateLoading(false);
    }
  };

  // Stage change inline
  const handleStageChange = async (oppId, newStage) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/opportunities/${oppId}/stage`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ new_stage: newStage }),
      });
      if (!res.ok) throw new Error('Failed to update stage');
      fetchOpportunities();
      fetchPipelineStats();
    } catch (err) {
      setError(err.message);
    }
  };

  // CSV Import
  const handleImport = async () => {
    if (!importFile) return;
    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const token = getValidToken();
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/opportunities/import-csv`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error('Import failed');
      const data = await res.json();
      setImportResults(data);
      if (data.created_count > 0) {
        fetchOpportunities();
        fetchPipelineStats();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setImportLoading(false);
    }
  };

  // Download template
  const handleDownloadTemplate = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/opportunities/csv-template`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to download template');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'grant_opportunities_template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
  };

  // Sort handler
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
    setPage(1);
  };

  const getStageBadge = (stage) => {
    const s = STAGES.find(st => st.value === stage);
    if (!s) return <span className="text-xs">{stage}</span>;
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>;
  };

  const getPriorityIcon = (priority) => {
    const p = PRIORITIES.find(pr => pr.value === priority);
    if (!p) return null;
    return <span className={`text-sm font-medium ${p.color}`}>{p.label}</span>;
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDeadline = (opp) => {
    if (opp.deadline && opp.deadline_type) return `${formatDate(opp.deadline)} (${opp.deadline_type})`;
    if (opp.deadline) return formatDate(opp.deadline);
    if (opp.deadline_type) return opp.deadline_type;
    return '-';
  };

  // Detail panel handlers
  const openDetail = async (opp) => {
    setSelectedOpp(opp);
    setEditForm({
      title: opp.title || '',
      funder_name: opp.funder_name || '',
      funder_type: opp.funder_type || '',
      grant_url: opp.grant_url || '',
      description: opp.description || '',
      amount_min: opp.amount_min != null ? opp.amount_min : '',
      amount_max: opp.amount_max != null ? opp.amount_max : '',
      deadline: opp.deadline || '',
      deadline_type: opp.deadline_type || '',
      deadline_notes: opp.deadline_notes || '',
      student_level: opp.student_level || '',
      loi_or_proposal: opp.loi_or_proposal || '',
      next_step_required: opp.next_step_required || '',
      notes: opp.notes || '',
      probability_of_win: opp.probability_of_win != null ? opp.probability_of_win : '',
      funder_contact_name: opp.funder_contact_name || '',
      funder_contact_email: opp.funder_contact_email || '',
      funder_contact_phone: opp.funder_contact_phone || '',
      funder_contact_linkedin: opp.funder_contact_linkedin || '',
      priority: opp.priority || 'medium',
      focus_areas: (opp.focus_areas || []).join('; '),
      eligibility_notes: opp.eligibility_notes || '',
      funder_requirements: opp.funder_requirements || '',
      internal_notes: opp.internal_notes || '',
      assigned_to: opp.assigned_to || '',
    });
    setDetailTab('overview');
    // Fetch history
    setHistoryLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/opportunities/${opp.id}/history`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.history || []);
      }
    } catch (err) {
      console.error('Failed to load history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedOpp(null);
    setEditForm(null);
    setHistory([]);
  };

  const handleSaveDetail = async () => {
    if (!selectedOpp || !editForm) return;
    setEditLoading(true);
    try {
      const payload = {
        ...editForm,
        amount_min: editForm.amount_min !== '' ? parseFloat(editForm.amount_min) : null,
        amount_max: editForm.amount_max !== '' ? parseFloat(editForm.amount_max) : null,
        deadline: editForm.deadline || null,
        deadline_type: editForm.deadline_type || null,
        deadline_notes: editForm.deadline_notes || null,
        student_level: editForm.student_level || null,
        loi_or_proposal: editForm.loi_or_proposal || null,
        next_step_required: editForm.next_step_required || null,
        notes: editForm.notes || null,
        probability_of_win: editForm.probability_of_win !== '' ? parseInt(editForm.probability_of_win) : null,
        funder_contact_name: editForm.funder_contact_name || null,
        funder_contact_email: editForm.funder_contact_email || null,
        funder_contact_phone: editForm.funder_contact_phone || null,
        funder_contact_linkedin: editForm.funder_contact_linkedin || null,
        assigned_to: editForm.assigned_to ? parseInt(editForm.assigned_to) : null,
        focus_areas: editForm.focus_areas ? editForm.focus_areas.split(';').map(s => s.trim()).filter(Boolean) : [],
      };
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/opportunities/${selectedOpp.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed to save');
      const updated = await res.json();
      setSelectedOpp(updated);
      setSuccessMsg('Opportunity updated');
      fetchOpportunities();
      fetchPipelineStats();
    } catch (err) {
      setError(err.message);
    } finally {
      setEditLoading(false);
    }
  };

  const handleArchiveToggle = async () => {
    if (!selectedOpp) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/opportunities/${selectedOpp.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_archived: !selectedOpp.is_archived }),
      });
      if (!res.ok) throw new Error('Failed to update');
      setSuccessMsg(selectedOpp.is_archived ? 'Opportunity restored' : 'Opportunity archived');
      closeDetail();
      fetchOpportunities();
      fetchPipelineStats();
    } catch (err) {
      setError(err.message);
    }
  };

  // ============================================================
  // Document Handlers (Phase 2a)
  // ============================================================

  const fetchDocuments = useCallback(async () => {
    try {
      setDocLoading(true);
      const params = new URLSearchParams({ page: docPage.toString(), per_page: '50' });
      if (docCategory) params.set('category', docCategory);
      if (docSearch) params.set('search', docSearch);
      if (docTag) params.set('tag', docTag);
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/documents?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to load documents');
      const data = await res.json();
      setDocuments(data.documents);
      setDocTotal(data.total);
      setDocTotalPages(data.total_pages);
    } catch (err) {
      setError(err.message);
    } finally {
      setDocLoading(false);
    }
  }, [docPage, docCategory, docSearch, docTag, getAuthHeaders]);

  const fetchTags = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/documents/tags`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setAllTags(data.tags || []);
      }
    } catch (err) {
      console.error('Failed to load tags:', err);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (accessChecked && !accessDenied && activeMainTab === 'documents') {
      fetchDocuments();
      fetchTags();
    }
  }, [fetchDocuments, fetchTags, accessChecked, accessDenied, activeMainTab]);

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', uploadForm.title || uploadFile.name);
      formData.append('category', uploadForm.category);
      formData.append('description', uploadForm.description);
      formData.append('tags', uploadForm.tags);
      const token = getValidToken();
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/documents/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Upload failed');
      }
      setSuccessMsg('Document uploaded successfully');
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadForm({ title: '', category: 'other', description: '', tags: '' });
      fetchDocuments();
      fetchTags();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploadLoading(false);
    }
  };

  const openDocDetail = async (doc) => {
    setDocDetailLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/documents/${doc.id}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to load document details');
      const data = await res.json();
      setShowDocDetail(data);
      setEditDocForm({
        title: data.title || '',
        category: data.category || 'other',
        description: data.description || '',
        tags: (data.tags || []).join(', '),
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setDocDetailLoading(false);
    }
  };

  const handleSaveDoc = async () => {
    if (!showDocDetail || !editDocForm) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/documents/${showDocDetail.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          title: editDocForm.title,
          category: editDocForm.category,
          description: editDocForm.description || null,
          tags: editDocForm.tags ? editDocForm.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        }),
      });
      if (!res.ok) throw new Error('Failed to update document');
      setSuccessMsg('Document updated');
      setShowDocDetail(null);
      setEditDocForm(null);
      fetchDocuments();
      fetchTags();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Delete this document? It can be recovered by an admin.')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/documents/${docId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to delete document');
      setSuccessMsg('Document deleted');
      setShowDocDetail(null);
      fetchDocuments();
    } catch (err) {
      setError(err.message);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // ============================================================
  // Proposal Handlers (Phase 2b)
  // ============================================================

  const fetchOppProposals = async (oppId) => {
    setOppProposalsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/opportunities/${oppId}/proposals`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setOppProposals(data.proposals || []);
      }
    } catch (err) {
      console.error('Failed to load proposals:', err);
    } finally {
      setOppProposalsLoading(false);
    }
  };

  const handleCreateProposal = async (oppId) => {
    if (!newProposalForm.title.trim()) { setError('Proposal title required'); return; }
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/opportunities/${oppId}/proposals`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newProposalForm),
      });
      if (!res.ok) throw new Error('Failed to create proposal');
      const data = await res.json();
      setShowNewProposalForm(false);
      setNewProposalForm({ title: '', proposal_type: 'full_proposal' });
      openProposalEditor(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const openProposalEditor = async (proposal) => {
    // Fetch latest from server
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/proposals/${proposal.id}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to load proposal');
      const data = await res.json();
      setActiveProposal(data);
      setProposalContent(data.content || '');
      setProposalTitle(data.title || '');
      setShowVersions(false);
      // Fetch versions
      fetchProposalVersions(data.id);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchProposalVersions = async (propId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/proposals/${propId}/versions`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setProposalVersions(data.versions || []);
      }
    } catch (err) {
      console.error('Failed to load versions:', err);
    }
  };

  const handleSaveProposal = async () => {
    if (!activeProposal) return;
    setProposalSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/proposals/${activeProposal.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          title: proposalTitle,
          content: proposalContent,
          expected_version: activeProposal.version,
        }),
      });
      if (res.status === 409) {
        const conflict = await res.json();
        const detail = conflict.detail || {};
        setError(`Version conflict: ${detail.last_saved_by || 'Someone'} saved at ${detail.last_saved_at ? new Date(detail.last_saved_at).toLocaleString() : 'unknown time'}. Copy your changes, then reload.`);
        return;
      }
      if (!res.ok) throw new Error('Failed to save');
      const data = await res.json();
      setActiveProposal(data);
      setSuccessMsg('Draft saved (v' + data.version + ')');
      fetchProposalVersions(data.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setProposalSaving(false);
    }
  };

  const handleSubmitForReview = async () => {
    if (!activeProposal) return;
    if (!window.confirm('Submit this proposal for superadmin review?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/proposals/${activeProposal.id}/submit`, {
        method: 'PUT',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to submit');
      const data = await res.json();
      setActiveProposal(data);
      setSuccessMsg('Proposal submitted for review');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReviewProposal = async (action) => {
    if (!activeProposal) return;
    const notes = action === 'request_revision'
      ? window.prompt('Revision notes for the grant writer:')
      : window.prompt('Approval notes (optional):');
    if (action === 'request_revision' && !notes) return; // require notes for revision
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/proposals/${activeProposal.id}/review`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ action, review_notes: notes || null }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Review failed');
      }
      const data = await res.json();
      setActiveProposal(data);
      setSuccessMsg(action === 'approve' ? 'Proposal approved' : 'Revision requested');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRestoreVersion = async (versionNum) => {
    if (!activeProposal) return;
    if (!window.confirm(`Restore version ${versionNum}? This will save it as a new version.`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/proposals/${activeProposal.id}/versions/${versionNum}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to load version');
      const verData = await res.json();
      setProposalContent(verData.content || '');
      setSuccessMsg(`Version ${versionNum} content restored. Click Save to keep it.`);
    } catch (err) {
      setError(err.message);
    }
  };

  const closeProposalEditor = () => {
    setActiveProposal(null);
    setProposalContent('');
    setProposalTitle('');
    setProposalVersions([]);
    setShowVersions(false);
    // Refresh proposals list if detail panel open
    if (selectedOpp) fetchOppProposals(selectedOpp.id);
  };

  const handleDeleteProposal = async (propId) => {
    if (!window.confirm('Delete this proposal and all its versions?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/proposals/${propId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to delete');
      setSuccessMsg('Proposal deleted');
      if (activeProposal && activeProposal.id === propId) closeProposalEditor();
      if (selectedOpp) fetchOppProposals(selectedOpp.id);
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePrintProposal = () => {
    window.print();
  };

  // --- Phase 3: AI handlers ---

  const handleAIGenerate = async () => {
    if (!activeProposal) return;
    setAiGenerating(true);
    setAiResult(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/proposals/${activeProposal.id}/ai/generate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ context_doc_ids: aiContextDocIds, instructions: aiInstructions }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Generation failed'); }
      const data = await res.json();
      setAiResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setAiGenerating(false);
    }
  };

  const applyAIGenerated = () => {
    if (aiResult?.generated_text) {
      if (proposalContent.trim() && !window.confirm('This will replace your current content. Continue?')) return;
      setProposalContent(aiResult.generated_text);
      setShowAIGenerateModal(false);
      setAiResult(null);
      setSuccessMsg('AI draft applied. Review and save when ready.');
    }
  };

  const appendAIGenerated = () => {
    if (aiResult?.generated_text) {
      setProposalContent(prev => prev + (prev ? '\n\n' : '') + aiResult.generated_text);
      setShowAIGenerateModal(false);
      setAiResult(null);
      setSuccessMsg('AI draft appended. Review and save when ready.');
    }
  };

  const handleExtractRequirements = async () => {
    if (!selectedOpp || !extractDocId) return;
    setExtracting(true);
    setExtractedReqs(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/opportunities/${selectedOpp.id}/extract-requirements`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ document_id: parseInt(extractDocId) }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Extraction failed'); }
      const data = await res.json();
      setExtractedReqs(data.requirements);
    } catch (err) {
      setError(err.message);
    } finally {
      setExtracting(false);
    }
  };

  const saveExtractedRequirements = async () => {
    if (!selectedOpp || !extractedReqs) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/grants/opportunities/${selectedOpp.id}/requirements`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ requirements: extractedReqs }),
      });
      if (!res.ok) throw new Error('Failed to save requirements');
      setSuccessMsg('Requirements saved to opportunity');
      setShowExtractReqModal(false);
      setExtractedReqs(null);
      // Refresh opportunity detail
      if (selectedOpp) {
        const r = await fetch(`${BACKEND_URL}/api/admin/grants/opportunities/${selectedOpp.id}`, { headers: getAuthHeaders() });
        if (r.ok) setSelectedOpp(await r.json());
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchBedrockUsage = useCallback(async () => {
    setUsageLoading(true);
    try {
      let url = `${BACKEND_URL}/api/admin/grants/bedrock-usage`;
      const params = new URLSearchParams();
      if (usageDateRange.start) params.set('start_date', usageDateRange.start);
      if (usageDateRange.end) params.set('end_date', usageDateRange.end);
      if (params.toString()) url += '?' + params.toString();
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to load usage');
      setBedrockUsage(await res.json());
    } catch (err) {
      console.error('Failed to load AI usage:', err);
    } finally {
      setUsageLoading(false);
    }
  }, [getAuthHeaders, usageDateRange]);

  // Fetch grant_costs_visible setting
  useEffect(() => {
    if (!accessChecked || accessDenied) return;
    fetch(`${BACKEND_URL}/api/admin/settings/grant_costs_visible`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.setting_value === 'true') setCostsTabVisible(true); })
      .catch(() => {});
  }, [accessChecked, accessDenied, getAuthHeaders]);

  const canSeeCosts = isSuperAdmin || costsTabVisible;

  useEffect(() => {
    if (accessChecked && !accessDenied && activeMainTab === 'ai-costs' && canSeeCosts) {
      fetchBedrockUsage();
    }
  }, [fetchBedrockUsage, accessChecked, accessDenied, activeMainTab, canSeeCosts]);

  // Fetch org-wide docs for AI context selection
  const [orgDocs, setOrgDocs] = useState([]);
  useEffect(() => {
    if (showAIGenerateModal || showExtractReqModal) {
      fetch(`${BACKEND_URL}/api/admin/grants/documents?per_page=100`, { headers: getAuthHeaders() })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data?.documents) setOrgDocs(data.documents); })
        .catch(() => {});
    }
  }, [showAIGenerateModal, showExtractReqModal, getAuthHeaders]);

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-700',
      in_review: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      submitted: 'bg-blue-100 text-blue-800',
      revision_requested: 'bg-red-100 text-red-800',
    };
    const labels = {
      draft: 'Draft',
      in_review: 'In Review',
      approved: 'Approved',
      submitted: 'Submitted',
      revision_requested: 'Revision Requested',
    };
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100'}`}>{labels[status] || status}</span>;
  };

  const getCategoryBadge = (cat) => {
    const c = DOC_CATEGORIES.find(dc => dc.value === cat);
    if (!c) return <span className="text-xs">{cat}</span>;
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">{c.icon} {c.label}</span>;
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="text-amber-600 ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  if (!accessChecked) return <div className="flex items-center justify-center h-screen"><div className="text-gray-500">Loading...</div></div>;
  if (accessDenied) return (
    <div className="flex items-center justify-center h-screen">
      <div className="text-center">
        <h2 className="text-xl font-bold text-red-600 mb-2">Access Denied</h2>
        <p className="text-gray-500">You do not have permission to access Grant Management.</p>
        <button onClick={() => navigate('/admin')} className="mt-4 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700">Back to Admin</button>
      </div>
    </div>
  );

  return (
    <AdminRouteGuard>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Grant Management</h1>
              <p className="text-sm text-gray-500 mt-1">Track grant opportunities and manage documents</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => navigate('/admin')}
                className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
              >
                ← Admin
              </button>
              {activeMainTab === 'pipeline' && (
                <>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700"
                  >
                    + New Opportunity
                  </button>
                  <button
                    onClick={() => setShowImportModal(true)}
                    className="px-3 py-1.5 text-sm bg-amber-100 text-amber-800 rounded hover:bg-amber-200"
                  >
                    Import CSV
                  </button>
                </>
              )}
              {activeMainTab === 'documents' && (
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700"
                >
                  + Upload Document
                </button>
              )}
            </div>
          </div>
          {/* Tab Navigation */}
          <div className="flex gap-1 mt-3 border-t border-gray-100 pt-3">
            <button
              onClick={() => setActiveMainTab('pipeline')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg ${activeMainTab === 'pipeline' ? 'bg-amber-100 text-amber-800 border-b-2 border-amber-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
            >
              Pipeline {pipelineStats ? `(${pipelineStats.total_active})` : ''}
            </button>
            <button
              onClick={() => setActiveMainTab('documents')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg ${activeMainTab === 'documents' ? 'bg-amber-100 text-amber-800 border-b-2 border-amber-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
            >
              Documents {docTotal > 0 ? `(${docTotal})` : ''}
            </button>
            {canSeeCosts && (
              <button
                onClick={() => setActiveMainTab('ai-costs')}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg ${activeMainTab === 'ai-costs' ? 'bg-amber-100 text-amber-800 border-b-2 border-amber-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
              >
                Costs
              </button>
            )}
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mx-4 sm:mx-6 mt-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {error}
          </div>
        )}
        {successMsg && (
          <div className="mx-4 sm:mx-6 mt-3 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-700">
            {successMsg}
          </div>
        )}

        <div className="px-4 sm:px-6 py-4 space-y-4">

          {/* ============ PIPELINE TAB ============ */}
          {activeMainTab === 'pipeline' && (<>
          {/* Pipeline Stats Cards */}
          {pipelineStats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-white rounded-lg border border-gray-200 p-3">
                <div className="text-2xl font-bold text-gray-900">{pipelineStats.total_active}</div>
                <div className="text-xs text-gray-500">Active Opportunities</div>
              </div>
              <div className="bg-white rounded-lg border border-amber-200 p-3">
                <div className="text-2xl font-bold text-amber-600">{pipelineStats.upcoming_deadlines}</div>
                <div className="text-xs text-gray-500">Deadlines (30 days)</div>
              </div>
              <div className="bg-white rounded-lg border border-red-200 p-3">
                <div className="text-2xl font-bold text-red-600">{pipelineStats.overdue}</div>
                <div className="text-xs text-gray-500">Overdue</div>
              </div>
              <div className="bg-white rounded-lg border border-green-200 p-3">
                <div className="text-2xl font-bold text-green-600">{pipelineStats.stage_counts?.awarded || 0}</div>
                <div className="text-xs text-gray-500">Awarded</div>
              </div>
              {pipelineStats.pending_review > 0 && (
                <div className="bg-white rounded-lg border border-purple-200 p-3">
                  <div className="text-2xl font-bold text-purple-600">{pipelineStats.pending_review}</div>
                  <div className="text-xs text-gray-500">Proposals Pending Review</div>
                </div>
              )}
            </div>
          )}

          {/* Quick Add Row */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="text-xs font-medium text-amber-700 mb-2">Quick Add</div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                placeholder="Grant title"
                value={quickAdd.title}
                onChange={(e) => setQuickAdd(prev => ({ ...prev, title: e.target.value }))}
                className="flex-1 px-3 py-1.5 text-sm border border-amber-300 rounded focus:ring-amber-500 focus:border-amber-500"
              />
              <input
                type="text"
                placeholder="Funder name"
                value={quickAdd.funder_name}
                onChange={(e) => setQuickAdd(prev => ({ ...prev, funder_name: e.target.value }))}
                className="flex-1 px-3 py-1.5 text-sm border border-amber-300 rounded focus:ring-amber-500 focus:border-amber-500"
              />
              <input
                type="date"
                value={quickAdd.deadline}
                onChange={(e) => setQuickAdd(prev => ({ ...prev, deadline: e.target.value }))}
                className="px-3 py-1.5 text-sm border border-amber-300 rounded focus:ring-amber-500 focus:border-amber-500"
              />
              <select
                value={quickAdd.stage}
                onChange={(e) => setQuickAdd(prev => ({ ...prev, stage: e.target.value }))}
                className="px-3 py-1.5 text-sm border border-amber-300 rounded focus:ring-amber-500 focus:border-amber-500"
              >
                {STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <button
                onClick={handleQuickAdd}
                disabled={quickAddLoading}
                className="px-4 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 whitespace-nowrap"
              >
                {quickAddLoading ? '...' : 'Add'}
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg border border-gray-200 p-3">
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                placeholder="Search title, funder, description..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500"
              />
              <select
                value={stageFilter}
                onChange={(e) => { setStageFilter(e.target.value); setPage(1); }}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded"
              >
                <option value="">All Stages</option>
                {STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <select
                value={priorityFilter}
                onChange={(e) => { setPriorityFilter(e.target.value); setPage(1); }}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded"
              >
                <option value="">All Priorities</option>
                {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
              <select
                value={assigneeFilter}
                onChange={(e) => { setAssigneeFilter(e.target.value); setPage(1); }}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded"
              >
                <option value="">All Assignees</option>
                {staff.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
              </select>
              <label className="flex items-center gap-1 text-sm text-gray-600 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => { setShowArchived(e.target.checked); setPage(1); }}
                  className="rounded border-gray-300 text-amber-600"
                />
                Archived
              </label>
            </div>
          </div>

          {/* Opportunities Table */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-gray-500">Loading opportunities...</div>
            ) : opportunities.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <div className="text-lg mb-2">No grant opportunities yet</div>
                <p className="text-sm">Use Quick Add above or Import CSV to get started.</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort('title')}>
                          Title <SortIcon column="title" />
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort('funder_name')}>
                          Funder <SortIcon column="funder_name" />
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Type
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort('stage')}>
                          Stage <SortIcon column="stage" />
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort('priority')}>
                          Priority <SortIcon column="priority" />
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort('deadline')}>
                          Deadline <SortIcon column="deadline" />
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort('amount_max')}>
                          Amount <SortIcon column="amount_max" />
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Student Level
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          LOI/Proposal
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Next Step
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Win %
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Assignee
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {opportunities.map(opp => {
                        const isOverdue = opp.deadline && new Date(opp.deadline) < new Date() && !['awarded', 'declined', 'deferred'].includes(opp.stage);
                        const priorityRowColor = isOverdue ? 'bg-red-50' :
                          opp.priority === 'urgent' ? 'bg-blue-50' :
                          opp.priority === 'high' ? 'bg-green-50' :
                          opp.priority === 'medium' ? 'bg-pink-50' :
                          opp.priority === 'low' ? 'bg-purple-50' : '';
                        return (
                          <tr key={opp.id} className={`hover:bg-amber-50 cursor-pointer ${priorityRowColor}`} onClick={() => openDetail(opp)}>
                            <td className="px-3 py-2 text-sm">
                              <div className="font-medium text-gray-900 max-w-xs truncate" title={opp.title}>{opp.title}</div>
                              {opp.grant_url && (
                                <a href={opp.grant_url} target="_blank" rel="noopener noreferrer" className="text-xs text-amber-600 hover:underline">
                                  Link ↗
                                </a>
                              )}
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-700 max-w-[150px] truncate" title={opp.funder_name}>
                              {opp.funder_name}
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-700 capitalize">
                              {opp.funder_type || '-'}
                            </td>
                            <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                              <select
                                value={opp.stage}
                                onChange={(e) => handleStageChange(opp.id, e.target.value)}
                                className="text-xs border border-gray-200 rounded px-1.5 py-0.5 focus:ring-amber-500 focus:border-amber-500"
                              >
                                {STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                              </select>
                            </td>
                            <td className="px-3 py-2 text-sm">
                              {getPriorityIcon(opp.priority)}
                            </td>
                            <td className="px-3 py-2 text-sm">
                              <span className={isOverdue ? 'text-red-600 font-medium' : 'text-gray-700'}>
                                {formatDeadline(opp)}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-700">
                              {opp.amount_min || opp.amount_max ? (
                                <span>
                                  {opp.amount_min && opp.amount_max
                                    ? `${formatCurrency(opp.amount_min)} - ${formatCurrency(opp.amount_max)}`
                                    : formatCurrency(opp.amount_max || opp.amount_min)}
                                </span>
                              ) : '-'}
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-700">
                              {opp.student_level || '-'}
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-700">
                              {opp.loi_or_proposal || '-'}
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-700 max-w-[180px] truncate" title={opp.next_step_required || ''}>
                              {opp.next_step_required || '-'}
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-700">
                              {opp.probability_of_win != null ? `${opp.probability_of_win}%` : '-'}
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-700">
                              {opp.assigned_to_name || '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Summary & Pagination */}
                <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-t border-gray-200">
                  <div className="text-xs text-gray-500">
                    Showing {opportunities.length} of {total} opportunities
                  </div>
                  {totalPages > 1 && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-100"
                      >
                        Prev
                      </button>
                      <span className="px-2 py-1 text-xs text-gray-600">
                        Page {page} of {totalPages}
                      </span>
                      <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-100"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          </>)}

          {/* ============ DOCUMENTS TAB ============ */}
          {activeMainTab === 'documents' && (
            <>
              {/* Document Filters */}
              <div className="bg-white rounded-lg border border-gray-200 p-3">
                <div className="flex flex-col gap-3">
                  {/* Category tabs */}
                  <div className="flex flex-wrap gap-1">
                    <button
                      onClick={() => { setDocCategory(''); setDocPage(1); }}
                      className={`px-3 py-1 text-xs rounded-full ${!docCategory ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >All</button>
                    {DOC_CATEGORIES.map(c => (
                      <button key={c.value}
                        onClick={() => { setDocCategory(c.value); setDocPage(1); }}
                        className={`px-3 py-1 text-xs rounded-full ${docCategory === c.value ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                      >{c.icon} {c.label}</button>
                    ))}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text" placeholder="Search documents..."
                      value={docSearch}
                      onChange={e => { setDocSearch(e.target.value); setDocPage(1); }}
                      className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500"
                    />
                    {allTags.length > 0 && (
                      <select value={docTag} onChange={e => { setDocTag(e.target.value); setDocPage(1); }}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded">
                        <option value="">All Tags</option>
                        {allTags.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    )}
                  </div>
                </div>
              </div>

              {/* Document Grid */}
              {docLoading ? (
                <div className="p-8 text-center text-gray-500">Loading documents...</div>
              ) : documents.length === 0 ? (
                <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
                  <div className="text-lg mb-2">No documents yet</div>
                  <p className="text-sm">Upload organizational documents like financials, mission statements, and boilerplate text.</p>
                  <button onClick={() => setShowUploadModal(true)}
                    className="mt-4 px-4 py-2 text-sm bg-amber-600 text-white rounded hover:bg-amber-700">
                    Upload First Document
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {documents.map(doc => (
                      <div key={doc.id}
                        onClick={() => openDocDetail(doc)}
                        className="bg-white rounded-lg border border-gray-200 p-4 hover:border-amber-300 hover:shadow-sm cursor-pointer transition-all">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h3 className="text-sm font-medium text-gray-900 truncate flex-1" title={doc.title}>{doc.title}</h3>
                          {getCategoryBadge(doc.category)}
                        </div>
                        {doc.description && (
                          <p className="text-xs text-gray-500 mb-2 line-clamp-2">{doc.description}</p>
                        )}
                        <div className="flex items-center gap-2 text-xs text-gray-400 mb-2">
                          <span>{doc.filename}</span>
                          <span>·</span>
                          <span>{formatFileSize(doc.file_size)}</span>
                        </div>
                        {doc.tags && doc.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {doc.tags.map(t => (
                              <span key={t} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600">{t}</span>
                            ))}
                          </div>
                        )}
                        <div className="text-xs text-gray-400 mt-2">
                          {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : ''}
                          {doc.uploaded_by_name && ` · ${doc.uploaded_by_name}`}
                          {doc.has_embedding && <span className="ml-1 text-green-500" title="Embedding generated">●</span>}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {docTotalPages > 1 && (
                    <div className="flex items-center justify-between bg-white rounded-lg border border-gray-200 px-3 py-2">
                      <div className="text-xs text-gray-500">Showing {documents.length} of {docTotal}</div>
                      <div className="flex gap-1">
                        <button onClick={() => setDocPage(p => Math.max(1, p - 1))} disabled={docPage === 1}
                          className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-100">Prev</button>
                        <span className="px-2 py-1 text-xs text-gray-600">Page {docPage} of {docTotalPages}</span>
                        <button onClick={() => setDocPage(p => Math.min(docTotalPages, p + 1))} disabled={docPage === docTotalPages}
                          className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-100">Next</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}

        </div>

        {/* ============ UPLOAD MODAL ============ */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-lg w-full">
              <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Upload Document</h2>
                <button onClick={() => { setShowUploadModal(false); setUploadFile(null); }} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">File *</label>
                  <input type="file" accept=".pdf,.docx,.xlsx,.doc,.xls,.txt,.csv"
                    onChange={e => {
                      setUploadFile(e.target.files[0] || null);
                      if (e.target.files[0] && !uploadForm.title) {
                        setUploadForm(p => ({ ...p, title: e.target.files[0].name.replace(/\.[^/.]+$/, '') }));
                      }
                    }}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100" />
                  <p className="text-xs text-gray-400 mt-1">PDF, DOCX, XLSX, TXT, CSV — max 25 MB</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input type="text" value={uploadForm.title}
                    onChange={e => setUploadForm(p => ({ ...p, title: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <select value={uploadForm.category}
                    onChange={e => setUploadForm(p => ({ ...p, category: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500">
                    {DOC_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.icon} {c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea rows={2} value={uploadForm.description}
                    onChange={e => setUploadForm(p => ({ ...p, description: e.target.value }))}
                    placeholder="Brief description of this document..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
                  <input type="text" value={uploadForm.tags}
                    onChange={e => setUploadForm(p => ({ ...p, tags: e.target.value }))}
                    placeholder="e.g., gates-2025, annual-report, stem"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={() => { setShowUploadModal(false); setUploadFile(null); }}
                    className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
                  <button onClick={handleUpload} disabled={!uploadFile || uploadLoading}
                    className="px-4 py-2 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50">
                    {uploadLoading ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============ DOCUMENT DETAIL SLIDE-OUT ============ */}
        {showDocDetail && (
          <div className="fixed inset-0 z-50 flex">
            <div className="flex-1 bg-black bg-opacity-30" onClick={() => { setShowDocDetail(null); setEditDocForm(null); }} />
            <div className="w-full max-w-md bg-white shadow-xl overflow-y-auto">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between z-10">
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg font-semibold text-gray-900 truncate">{showDocDetail.title}</h2>
                  <div className="text-xs text-gray-500 mt-0.5">{showDocDetail.filename} · {formatFileSize(showDocDetail.file_size)}</div>
                </div>
                <button onClick={() => { setShowDocDetail(null); setEditDocForm(null); }}
                  className="ml-2 text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
              </div>

              <div className="p-5 space-y-4">
                {/* Download */}
                {showDocDetail.download_url && (
                  <a href={showDocDetail.download_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 text-sm bg-amber-600 text-white rounded hover:bg-amber-700">
                    Download File
                  </a>
                )}

                {/* Edit Form */}
                {editDocForm && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
                      <input type="text" value={editDocForm.title}
                        onChange={e => setEditDocForm(p => ({ ...p, title: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
                      <select value={editDocForm.category}
                        onChange={e => setEditDocForm(p => ({ ...p, category: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500">
                        {DOC_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.icon} {c.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
                      <textarea rows={2} value={editDocForm.description}
                        onChange={e => setEditDocForm(p => ({ ...p, description: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Tags (comma-separated)</label>
                      <input type="text" value={editDocForm.tags}
                        onChange={e => setEditDocForm(p => ({ ...p, tags: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <button onClick={() => handleDeleteDoc(showDocDetail.id)}
                        className="text-sm text-red-500 hover:text-red-700">Delete</button>
                      <div className="flex gap-2">
                        <button onClick={() => { setShowDocDetail(null); setEditDocForm(null); }}
                          className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
                        <button onClick={handleSaveDoc}
                          className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700">Save</button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Metadata */}
                <div className="text-xs text-gray-400 border-t border-gray-100 pt-3">
                  {getCategoryBadge(showDocDetail.category)}
                  <div className="mt-2">
                    Uploaded {showDocDetail.created_at ? new Date(showDocDetail.created_at).toLocaleString() : '—'}
                    {showDocDetail.uploaded_by_name && ` by ${showDocDetail.uploaded_by_name}`}
                  </div>
                  {showDocDetail.has_embedding && <div className="mt-1 text-green-600">Embedding generated (searchable)</div>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">New Grant Opportunity</h2>
                <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
              </div>
              <form onSubmit={handleCreate} className="p-4 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <input type="text" required value={createForm.title} onChange={e => setCreateForm(p => ({ ...p, title: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Funder Name *</label>
                    <input type="text" required value={createForm.funder_name} onChange={e => setCreateForm(p => ({ ...p, funder_name: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Funder Type</label>
                    <select value={createForm.funder_type} onChange={e => setCreateForm(p => ({ ...p, funder_type: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500">
                      <option value="">Select...</option>
                      {FUNDER_TYPES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Grant URL</label>
                    <input type="url" value={createForm.grant_url} onChange={e => setCreateForm(p => ({ ...p, grant_url: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Deadline Date</label>
                    <input type="date" value={createForm.deadline} onChange={e => setCreateForm(p => ({ ...p, deadline: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Deadline Type</label>
                    <input type="text" value={createForm.deadline_type}
                      placeholder="e.g., Rolling, Spring/Fall, Aug–Sep, Cycle varies"
                      onChange={e => setCreateForm(p => ({ ...p, deadline_type: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Student Level</label>
                    <select value={createForm.student_level} onChange={e => setCreateForm(p => ({ ...p, student_level: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500">
                      <option value="">Select...</option>
                      {STUDENT_LEVELS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">LOI / Full Proposal</label>
                    <select value={createForm.loi_or_proposal} onChange={e => setCreateForm(p => ({ ...p, loi_or_proposal: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500">
                      <option value="">Select...</option>
                      {LOI_OR_PROPOSAL.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Probability of Win (%)</label>
                    <input type="number" min="0" max="100" value={createForm.probability_of_win}
                      onChange={e => setCreateForm(p => ({ ...p, probability_of_win: e.target.value }))}
                      placeholder="0-100"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Stage</label>
                    <select value={createForm.stage} onChange={e => setCreateForm(p => ({ ...p, stage: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500">
                      {STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                    <select value={createForm.priority} onChange={e => setCreateForm(p => ({ ...p, priority: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500">
                      {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount Min ($)</label>
                    <input type="number" step="0.01" value={createForm.amount_min} onChange={e => setCreateForm(p => ({ ...p, amount_min: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount Max ($)</label>
                    <input type="number" step="0.01" value={createForm.amount_max} onChange={e => setCreateForm(p => ({ ...p, amount_max: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                    <select value={createForm.assigned_to} onChange={e => setCreateForm(p => ({ ...p, assigned_to: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500">
                      <option value="">Unassigned</option>
                      {staff.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2 border-t border-gray-200 pt-3 mt-1">
                    <div className="text-sm font-medium text-gray-700 mb-2">Funder Contact</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Name</label>
                        <input type="text" value={createForm.funder_contact_name} onChange={e => setCreateForm(p => ({ ...p, funder_contact_name: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Email</label>
                        <input type="email" value={createForm.funder_contact_email} onChange={e => setCreateForm(p => ({ ...p, funder_contact_email: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Phone</label>
                        <input type="tel" value={createForm.funder_contact_phone} onChange={e => setCreateForm(p => ({ ...p, funder_contact_phone: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">LinkedIn Profile</label>
                        <input type="url" value={createForm.funder_contact_linkedin} onChange={e => setCreateForm(p => ({ ...p, funder_contact_linkedin: e.target.value }))}
                          placeholder="https://linkedin.com/in/..."
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                      </div>
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea rows={3} value={createForm.description} onChange={e => setCreateForm(p => ({ ...p, description: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Focus Areas (semicolon-separated)</label>
                    <input type="text" placeholder="STEM education; diversity; workforce development" value={createForm.focus_areas}
                      onChange={e => setCreateForm(p => ({ ...p, focus_areas: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Eligibility Notes</label>
                    <textarea rows={2} value={createForm.eligibility_notes} onChange={e => setCreateForm(p => ({ ...p, eligibility_notes: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Funder Requirements</label>
                    <textarea rows={2} value={createForm.funder_requirements} onChange={e => setCreateForm(p => ({ ...p, funder_requirements: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Next Step Required</label>
                    <input type="text" value={createForm.next_step_required} onChange={e => setCreateForm(p => ({ ...p, next_step_required: e.target.value }))}
                      placeholder="e.g., Draft LOI by May 1"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea rows={2} value={createForm.notes} onChange={e => setCreateForm(p => ({ ...p, notes: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Internal Notes</label>
                    <textarea rows={2} value={createForm.internal_notes} onChange={e => setCreateForm(p => ({ ...p, internal_notes: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                  <button type="button" onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
                  <button type="submit" disabled={createLoading}
                    className="px-4 py-2 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50">
                    {createLoading ? 'Creating...' : 'Create Opportunity'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Import CSV Modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-lg w-full">
              <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Import CSV</h2>
                <button onClick={() => { setShowImportModal(false); setImportFile(null); setImportResults(null); }}
                  className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <button onClick={handleDownloadTemplate}
                    className="text-sm text-amber-600 hover:text-amber-800 underline">
                    Download CSV template
                  </button>
                </div>

                {/* Field Reference */}
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs space-y-2">
                  <div className="font-medium text-amber-800">CSV Field Reference</div>
                  <div>
                    <span className="font-medium text-gray-700">Required:</span>{' '}
                    <span className="text-gray-600">title, funder_name</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Stage</span>{' '}
                    <span className="text-gray-500">(defaults to "researching" if blank):</span>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {STAGES.map(s => (
                        <span key={s.value} className={`inline-block px-1.5 py-0.5 rounded ${s.color}`}>{s.value}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Priority</span>{' '}
                    <span className="text-gray-500">(defaults to "medium" if blank):</span>{' '}
                    <span className="text-gray-600">low, medium, high, urgent</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Funder Type:</span>{' '}
                    <span className="text-gray-600">foundation, government, corporate, other</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Deadline:</span>{' '}
                    <span className="text-gray-600">YYYY-MM-DD or MM/DD/YYYY (optional — leave blank if no specific date)</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Deadline Type:</span>{' '}
                    <span className="text-gray-600">Free text (e.g., Rolling, Spring/Fall, Aug–Sep, Cycle varies, Invite)</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Deadline Notes:</span>{' '}
                    <span className="text-gray-600">Additional deadline context (optional)</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Focus Areas:</span>{' '}
                    <span className="text-gray-600">Semicolon-separated (e.g., "STEM education; diversity")</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Student Level:</span>{' '}
                    <span className="text-gray-600">College, Professional, College + Professional, High School, High School + College, College + Bridge, Bridge</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">LOI / Full Proposal:</span>{' '}
                    <span className="text-gray-600">LOI, Full Proposal, LOI + Full Proposal, Invite Only, Application</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Probability of Win:</span>{' '}
                    <span className="text-gray-600">0-100 (numeric, no % sign needed)</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Amounts:</span>{' '}
                    <span className="text-gray-600">Numeric only, no $ or commas needed (e.g., 50000)</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Funder Contact:</span>{' '}
                    <span className="text-gray-600">funder_contact_name, funder_contact_email, funder_contact_phone, funder_contact_linkedin (all optional)</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Next Step Required, Notes:</span>{' '}
                    <span className="text-gray-600">Free text (optional)</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select CSV File</label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={e => setImportFile(e.target.files[0])}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100"
                  />
                </div>
                {importResults && (
                  <div className="bg-gray-50 rounded p-3 text-sm">
                    <div className="font-medium text-gray-900 mb-1">Import Results</div>
                    <div className="text-green-700">Created: {importResults.created_count}</div>
                    {importResults.error_count > 0 && (
                      <div className="text-red-700 mt-1">
                        Errors: {importResults.error_count}
                        <ul className="list-disc list-inside mt-1">
                          {importResults.errors.map((err, i) => (
                            <li key={i}>Row {err.row}: {err.error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <button onClick={() => { setShowImportModal(false); setImportFile(null); setImportResults(null); }}
                    className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Close</button>
                  {!importResults && (
                    <button onClick={handleImport} disabled={!importFile || importLoading}
                      className="px-4 py-2 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50">
                      {importLoading ? 'Importing...' : 'Import'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {/* ============ AI COSTS TAB (Superadmin) ============ */}
        {activeMainTab === 'ai-costs' && canSeeCosts && (
          <div className="space-y-4 px-4 sm:px-6 pb-6">
            <div className="flex items-center gap-3 bg-white rounded-lg border border-gray-200 p-3">
              <label className="text-sm text-gray-600">From</label>
              <input type="date" value={usageDateRange.start}
                onChange={e => setUsageDateRange(p => ({ ...p, start: e.target.value }))}
                className="px-2 py-1 text-sm border border-gray-300 rounded" />
              <label className="text-sm text-gray-600">To</label>
              <input type="date" value={usageDateRange.end}
                onChange={e => setUsageDateRange(p => ({ ...p, end: e.target.value }))}
                className="px-2 py-1 text-sm border border-gray-300 rounded" />
              <button onClick={fetchBedrockUsage} className="px-3 py-1.5 text-xs bg-amber-600 text-white rounded hover:bg-amber-700">
                Refresh
              </button>
            </div>

            {usageLoading && <div className="text-center text-gray-400 py-8">Loading...</div>}

            {bedrockUsage && !usageLoading && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <div className="text-2xl font-bold text-gray-900">${bedrockUsage.summary.total_cost.toFixed(4)}</div>
                    <div className="text-xs text-gray-500">Total Cost</div>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <div className="text-2xl font-bold text-violet-600">{bedrockUsage.summary.total_requests}</div>
                    <div className="text-xs text-gray-500">Total Requests</div>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <div className="text-2xl font-bold text-blue-600">{(bedrockUsage.summary.total_input_tokens / 1000).toFixed(1)}k</div>
                    <div className="text-xs text-gray-500">Input Tokens</div>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <div className="text-2xl font-bold text-green-600">{(bedrockUsage.summary.total_output_tokens / 1000).toFixed(1)}k</div>
                    <div className="text-xs text-gray-500">Output Tokens</div>
                  </div>
                </div>

                {/* By Type Breakdown */}
                {Object.keys(bedrockUsage.summary.by_type).length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">By Request Type</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {Object.entries(bedrockUsage.summary.by_type).map(([type, info]) => (
                        <div key={type} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm text-gray-700 capitalize">{type.replace('_', ' ')}</span>
                          <div className="text-right">
                            <span className="text-sm font-medium text-gray-900">${info.cost.toFixed(4)}</span>
                            <span className="text-xs text-gray-400 ml-2">({info.count} calls)</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Usage Table */}
                <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Opportunity</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Type</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">By</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Tokens In</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Tokens Out</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Cost</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {bedrockUsage.usage.map(u => (
                        <tr key={u.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-600">{u.created_at ? new Date(u.created_at).toLocaleString() : '-'}</td>
                          <td className="px-3 py-2 text-gray-800 truncate max-w-xs">{u.opportunity_title || '-'}</td>
                          <td className="px-3 py-2 capitalize text-gray-600">{(u.request_type || '').replace('_', ' ')}</td>
                          <td className="px-3 py-2 text-gray-600">{u.requested_by_name || '-'}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{(u.input_tokens || 0).toLocaleString()}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{(u.output_tokens || 0).toLocaleString()}</td>
                          <td className="px-3 py-2 text-right font-medium text-gray-900">${u.cost_usd.toFixed(4)}</td>
                        </tr>
                      ))}
                      {bedrockUsage.usage.length === 0 && (
                        <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-400">No AI usage yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* ============ AI GENERATE MODAL ============ */}
        {showAIGenerateModal && (
          <div className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto">
              <div className="p-5 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Generate Draft</h3>
                  <button onClick={() => setShowAIGenerateModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Claude will use the opportunity details, attached documents, and your instructions to generate a draft.</p>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Instructions (optional)</label>
                  <textarea rows={4} value={aiInstructions} onChange={e => setAiInstructions(e.target.value)}
                    placeholder="e.g., Focus on our STEM program outcomes. Keep the tone formal. Emphasize diversity metrics..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-violet-500 focus:border-violet-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Additional Context Documents</label>
                  <p className="text-xs text-gray-400 mb-2">Documents attached to this opportunity are included automatically. Select additional org-wide docs below.</p>
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded p-2 space-y-1">
                    {orgDocs.map(doc => (
                      <label key={doc.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input type="checkbox"
                          checked={aiContextDocIds.includes(doc.id)}
                          onChange={e => {
                            if (e.target.checked) setAiContextDocIds(prev => [...prev, doc.id]);
                            else setAiContextDocIds(prev => prev.filter(id => id !== doc.id));
                          }}
                          className="rounded text-violet-600" />
                        <span className="truncate">{doc.title}</span>
                        <span className="text-xs text-gray-400 ml-auto">{doc.category}</span>
                      </label>
                    ))}
                    {orgDocs.length === 0 && <div className="text-xs text-gray-400 text-center py-2">No documents uploaded yet</div>}
                  </div>
                </div>

                {aiResult && (
                  <div className="border border-green-200 bg-green-50 rounded p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-green-800">Draft Generated</span>
                      <span className="text-xs text-gray-500">{aiResult.input_tokens + aiResult.output_tokens} tokens · ${aiResult.cost_usd.toFixed(4)}</span>
                    </div>
                    <div className="max-h-48 overflow-y-auto text-sm text-gray-700 whitespace-pre-wrap bg-white rounded p-2 border border-green-100">
                      {aiResult.generated_text?.slice(0, 1000)}{aiResult.generated_text?.length > 1000 ? '...' : ''}
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button onClick={applyAIGenerated}
                        className="flex-1 px-3 py-1.5 text-xs bg-violet-600 text-white rounded hover:bg-violet-700">
                        Replace Content
                      </button>
                      <button onClick={appendAIGenerated}
                        className="flex-1 px-3 py-1.5 text-xs border border-violet-300 text-violet-700 rounded hover:bg-violet-50">
                        Append to Content
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={() => setShowAIGenerateModal(false)}
                    className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
                  <button onClick={handleAIGenerate} disabled={aiGenerating}
                    className="px-4 py-2 text-sm bg-violet-600 text-white rounded hover:bg-violet-700 disabled:opacity-50">
                    {aiGenerating ? 'Generating...' : 'Generate Draft'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============ EXTRACT REQUIREMENTS MODAL ============ */}
        {showExtractReqModal && (
          <div className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto">
              <div className="p-5 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Extract Requirements from RFP</h3>
                  <button onClick={() => { setShowExtractReqModal(false); setExtractedReqs(null); }} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Select an uploaded RFP document. Claude will extract structured requirements for you to review and save.</p>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">RFP Document</label>
                  <select value={extractDocId} onChange={e => setExtractDocId(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-violet-500 focus:border-violet-500">
                    <option value="">Select a document...</option>
                    {orgDocs.map(doc => (
                      <option key={doc.id} value={doc.id}>{doc.title} ({doc.category})</option>
                    ))}
                  </select>
                </div>

                {extractedReqs && (
                  <div className="border border-blue-200 bg-blue-50 rounded p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-blue-800">Extracted Requirements ({extractedReqs.length})</span>
                    </div>
                    <div className="max-h-60 overflow-y-auto space-y-2">
                      {extractedReqs.map((req, i) => (
                        <div key={i} className="bg-white rounded p-2 border border-blue-100 text-sm">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">{req.section}</span>
                            {req.mandatory && <span className="text-xs text-red-600 font-medium">Required</span>}
                            {req.word_limit && <span className="text-xs text-gray-400">{req.word_limit} words</span>}
                          </div>
                          <div className="text-gray-700">{req.requirement}</div>
                        </div>
                      ))}
                    </div>
                    <button onClick={saveExtractedRequirements}
                      className="mt-3 w-full px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">
                      Save Requirements to Opportunity
                    </button>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={() => { setShowExtractReqModal(false); setExtractedReqs(null); }}
                    className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
                  <button onClick={handleExtractRequirements} disabled={extracting || !extractDocId}
                    className="px-4 py-2 text-sm bg-violet-600 text-white rounded hover:bg-violet-700 disabled:opacity-50">
                    {extracting ? 'Extracting...' : 'Extract Requirements'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Detail Slide-Out Panel */}
        {selectedOpp && editForm && (
          <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-black bg-opacity-30" onClick={closeDetail} />
            <div className="relative w-full max-w-2xl bg-white shadow-xl overflow-y-auto animate-slide-in-right">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 z-10">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0 pr-4">
                    <h2 className="text-lg font-bold text-gray-900 truncate">{selectedOpp.title}</h2>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm text-gray-500">{selectedOpp.funder_name}</span>
                      {getStageBadge(selectedOpp.stage)}
                    </div>
                  </div>
                  <button onClick={closeDetail} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
                </div>
                {/* Tabs */}
                <div className="flex gap-4 mt-3 border-b border-gray-100 -mb-4 pb-0">
                  <button
                    onClick={() => setDetailTab('overview')}
                    className={`pb-2 text-sm font-medium border-b-2 ${detailTab === 'overview' ? 'border-amber-500 text-amber-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                  >Overview</button>
                  <button
                    onClick={() => setDetailTab('history')}
                    className={`pb-2 text-sm font-medium border-b-2 ${detailTab === 'history' ? 'border-amber-500 text-amber-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                  >History ({history.length})</button>
                  <button
                    onClick={() => { setDetailTab('proposals'); fetchOppProposals(selectedOpp.id); }}
                    className={`pb-2 text-sm font-medium border-b-2 ${detailTab === 'proposals' ? 'border-amber-500 text-amber-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                  >Proposals {oppProposals.length > 0 ? `(${oppProposals.length})` : ''}</button>
                </div>
              </div>

              {/* Overview Tab */}
              {detailTab === 'overview' && (
                <div className="p-5 space-y-5">
                  {/* Core Info */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
                      <input type="text" value={editForm.title} onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Funder Name</label>
                      <input type="text" value={editForm.funder_name} onChange={e => setEditForm(p => ({ ...p, funder_name: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Funder Type</label>
                      <select value={editForm.funder_type} onChange={e => setEditForm(p => ({ ...p, funder_type: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500">
                        <option value="">Select...</option>
                        {FUNDER_TYPES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Grant URL</label>
                      <input type="url" value={editForm.grant_url} onChange={e => setEditForm(p => ({ ...p, grant_url: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                    </div>
                  </div>

                  {/* Deadline & Tracking */}
                  <div className="border-t border-gray-100 pt-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Deadline & Tracking</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Deadline Date</label>
                        <input type="date" value={editForm.deadline} onChange={e => setEditForm(p => ({ ...p, deadline: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Deadline Type</label>
                        <input type="text" value={editForm.deadline_type} placeholder="e.g., Rolling, Spring/Fall, Aug–Sep"
                          onChange={e => setEditForm(p => ({ ...p, deadline_type: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Priority</label>
                        <select value={editForm.priority} onChange={e => setEditForm(p => ({ ...p, priority: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500">
                          {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Assigned To</label>
                        <select value={editForm.assigned_to} onChange={e => setEditForm(p => ({ ...p, assigned_to: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500">
                          <option value="">Unassigned</option>
                          {staff.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Student Level</label>
                        <select value={editForm.student_level} onChange={e => setEditForm(p => ({ ...p, student_level: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500">
                          <option value="">Select...</option>
                          {STUDENT_LEVELS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">LOI / Full Proposal</label>
                        <select value={editForm.loi_or_proposal} onChange={e => setEditForm(p => ({ ...p, loi_or_proposal: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500">
                          <option value="">Select...</option>
                          {LOI_OR_PROPOSAL.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Probability of Win (%)</label>
                        <input type="number" min="0" max="100" value={editForm.probability_of_win} placeholder="0-100"
                          onChange={e => setEditForm(p => ({ ...p, probability_of_win: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                      </div>
                    </div>
                  </div>

                  {/* Amounts */}
                  <div className="border-t border-gray-100 pt-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Funding</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Amount Min ($)</label>
                        <input type="number" step="0.01" value={editForm.amount_min}
                          onChange={e => setEditForm(p => ({ ...p, amount_min: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Amount Max ($)</label>
                        <input type="number" step="0.01" value={editForm.amount_max}
                          onChange={e => setEditForm(p => ({ ...p, amount_max: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                      </div>
                    </div>
                  </div>

                  {/* Funder Contact */}
                  <div className="border-t border-gray-100 pt-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Funder Contact</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
                        <input type="text" value={editForm.funder_contact_name}
                          onChange={e => setEditForm(p => ({ ...p, funder_contact_name: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                        <input type="email" value={editForm.funder_contact_email}
                          onChange={e => setEditForm(p => ({ ...p, funder_contact_email: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Phone</label>
                        <input type="tel" value={editForm.funder_contact_phone}
                          onChange={e => setEditForm(p => ({ ...p, funder_contact_phone: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">LinkedIn</label>
                        <input type="url" value={editForm.funder_contact_linkedin} placeholder="https://linkedin.com/in/..."
                          onChange={e => setEditForm(p => ({ ...p, funder_contact_linkedin: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                      </div>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="border-t border-gray-100 pt-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Details</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
                        <textarea rows={3} value={editForm.description}
                          onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Focus Areas (semicolon-separated)</label>
                        <input type="text" value={editForm.focus_areas}
                          onChange={e => setEditForm(p => ({ ...p, focus_areas: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Eligibility Notes</label>
                        <textarea rows={2} value={editForm.eligibility_notes}
                          onChange={e => setEditForm(p => ({ ...p, eligibility_notes: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Funder Requirements</label>
                        <textarea rows={2} value={editForm.funder_requirements}
                          onChange={e => setEditForm(p => ({ ...p, funder_requirements: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                        <button onClick={() => { setExtractDocId(''); setExtractedReqs(null); setShowExtractReqModal(true); }}
                          className="mt-1 px-3 py-1 text-xs text-violet-600 border border-violet-200 rounded hover:bg-violet-50">
                          Extract Requirements from RFP
                        </button>
                        {selectedOpp?.funder_requirements_structured?.length > 0 && (
                          <div className="mt-2 p-2 bg-blue-50 border border-blue-100 rounded">
                            <div className="text-xs font-medium text-blue-700 mb-1">Structured Requirements ({selectedOpp.funder_requirements_structured.length})</div>
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                              {selectedOpp.funder_requirements_structured.map((req, i) => (
                                <div key={i} className="text-xs text-blue-600">
                                  <span className="font-medium">[{req.section}]</span> {req.requirement}
                                  {req.mandatory && <span className="text-red-500 ml-1">*</span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Next Step Required</label>
                        <input type="text" value={editForm.next_step_required} placeholder="e.g., Draft LOI by May 1"
                          onChange={e => setEditForm(p => ({ ...p, next_step_required: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
                        <textarea rows={2} value={editForm.notes}
                          onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Internal Notes</label>
                        <textarea rows={2} value={editForm.internal_notes}
                          onChange={e => setEditForm(p => ({ ...p, internal_notes: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                      </div>
                    </div>
                  </div>

                  {/* Action Bar */}
                  <div className="border-t border-gray-200 pt-4 flex items-center justify-between">
                    <button onClick={handleArchiveToggle}
                      className="text-sm text-gray-500 hover:text-red-600">
                      {selectedOpp.is_archived ? 'Restore from Archive' : 'Archive'}
                    </button>
                    <div className="flex gap-2">
                      <button onClick={closeDetail}
                        className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
                      <button onClick={handleSaveDetail} disabled={editLoading}
                        className="px-4 py-2 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50">
                        {editLoading ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>

                  {/* Metadata */}
                  <div className="text-xs text-gray-400 pt-2">
                    Created {selectedOpp.created_at ? new Date(selectedOpp.created_at).toLocaleDateString() : '—'}
                    {selectedOpp.created_by_name && ` by ${selectedOpp.created_by_name}`}
                    {selectedOpp.updated_at && ` · Updated ${new Date(selectedOpp.updated_at).toLocaleDateString()}`}
                  </div>
                </div>
              )}

              {/* History Tab */}
              {detailTab === 'history' && (
                <div className="p-5">
                  {historyLoading ? (
                    <div className="text-center text-gray-500 py-8">Loading history...</div>
                  ) : history.length === 0 ? (
                    <div className="text-center text-gray-400 py-8">No stage changes recorded</div>
                  ) : (
                    <div className="relative">
                      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
                      <div className="space-y-4">
                        {history.map((h, i) => (
                          <div key={h.id} className="relative pl-10">
                            <div className="absolute left-2.5 top-1.5 w-3 h-3 rounded-full bg-amber-400 border-2 border-white" />
                            <div className="bg-gray-50 rounded-lg p-3">
                              <div className="flex items-center gap-2 text-sm">
                                {h.from_stage ? (
                                  <>
                                    {getStageBadge(h.from_stage)}
                                    <span className="text-gray-400">→</span>
                                    {getStageBadge(h.to_stage)}
                                  </>
                                ) : (
                                  <>
                                    <span className="text-gray-500 text-xs">Created as</span>
                                    {getStageBadge(h.to_stage)}
                                  </>
                                )}
                              </div>
                              {h.notes && <div className="text-xs text-gray-600 mt-1">{h.notes}</div>}
                              <div className="text-xs text-gray-400 mt-1">
                                {h.changed_at ? new Date(h.changed_at).toLocaleString() : '—'}
                                {h.changed_by_name && ` · ${h.changed_by_name}`}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Proposals Tab */}
              {detailTab === 'proposals' && (
                <div className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-gray-700">Proposals & LOIs</h3>
                    <button onClick={() => setShowNewProposalForm(true)}
                      className="px-3 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700">+ New Draft</button>
                  </div>

                  {/* New proposal form */}
                  {showNewProposalForm && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 space-y-2">
                      <input type="text" placeholder="Proposal title..." value={newProposalForm.title}
                        onChange={e => setNewProposalForm(p => ({ ...p, title: e.target.value }))}
                        className="w-full px-3 py-1.5 text-sm border border-amber-300 rounded focus:ring-amber-500 focus:border-amber-500" />
                      <div className="flex gap-2">
                        <select value={newProposalForm.proposal_type}
                          onChange={e => setNewProposalForm(p => ({ ...p, proposal_type: e.target.value }))}
                          className="px-3 py-1.5 text-sm border border-amber-300 rounded">
                          <option value="full_proposal">Full Proposal</option>
                          <option value="loi">LOI</option>
                          <option value="budget_narrative">Budget Narrative</option>
                        </select>
                        <button onClick={() => handleCreateProposal(selectedOpp.id)}
                          className="px-3 py-1.5 text-xs bg-amber-600 text-white rounded hover:bg-amber-700">Create</button>
                        <button onClick={() => setShowNewProposalForm(false)}
                          className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
                      </div>
                    </div>
                  )}

                  {oppProposalsLoading ? (
                    <div className="text-center text-gray-500 py-6">Loading...</div>
                  ) : oppProposals.length === 0 ? (
                    <div className="text-center text-gray-400 py-6">
                      <p className="mb-2">No proposals yet</p>
                      <p className="text-xs">Create a draft to start writing.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {oppProposals.map(p => (
                        <div key={p.id}
                          className="bg-white border border-gray-200 rounded-lg p-3 hover:border-amber-300 cursor-pointer transition-all"
                          onClick={() => { closeDetail(); openProposalEditor(p); }}>
                          <div className="flex items-center justify-between">
                            <div className="font-medium text-sm text-gray-900 truncate">{p.title}</div>
                            {getStatusBadge(p.status)}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                            <span className="capitalize">{(p.proposal_type || '').replace('_', ' ')}</span>
                            <span>v{p.version}</span>
                            <span>{p.updated_at ? new Date(p.updated_at).toLocaleDateString() : ''}</span>
                            {p.created_by_name && <span>{p.created_by_name}</span>}
                          </div>
                          {p.review_notes && p.status === 'revision_requested' && (
                            <div className="mt-2 p-2 bg-red-50 border border-red-100 rounded text-xs text-red-700">
                              Reviewer: {p.review_notes}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ============ PROPOSAL EDITOR (Full-Width) ============ */}
        {activeProposal && (
          <div className="fixed inset-0 z-50 bg-gray-50 flex flex-col print:static print:bg-white">
            {/* Editor Header */}
            <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between print:hidden">
              <div className="flex items-center gap-3 min-w-0">
                <button onClick={closeProposalEditor}
                  className="text-gray-400 hover:text-gray-600 text-lg">← Back</button>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold text-gray-900 truncate">{activeProposal.title}</h2>
                    {getStatusBadge(activeProposal.status)}
                  </div>
                  <div className="text-xs text-gray-400">
                    v{activeProposal.version} · {activeProposal.proposal_type?.replace('_', ' ')}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setShowVersions(!showVersions)}
                  className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50">
                  Versions ({proposalVersions.length})
                </button>
                {(activeProposal.status === 'draft' || activeProposal.status === 'revision_requested') && (
                  <>
                    <button onClick={() => { setAiInstructions(''); setAiContextDocIds([]); setAiResult(null); setShowAIGenerateModal(true); }}
                      className="px-3 py-1.5 text-xs bg-violet-600 text-white rounded hover:bg-violet-700">
                      Generate
                    </button>
                    <button onClick={handleSaveProposal} disabled={proposalSaving}
                      className="px-3 py-1.5 text-xs bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50">
                      {proposalSaving ? 'Saving...' : 'Save Draft'}
                    </button>
                    <button onClick={handleSubmitForReview}
                      className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700">
                      Submit for Review
                    </button>
                  </>
                )}
                {activeProposal.status === 'in_review' && isSuperAdmin && (
                  <>
                    <button onClick={() => handleReviewProposal('approve')}
                      className="px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700">
                      Approve
                    </button>
                    <button onClick={() => handleReviewProposal('request_revision')}
                      className="px-3 py-1.5 text-xs bg-red-600 text-white rounded hover:bg-red-700">
                      Request Revision
                    </button>
                  </>
                )}
                {activeProposal.status === 'approved' && (
                  <button onClick={handlePrintProposal}
                    className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">
                    Export PDF
                  </button>
                )}
                <button onClick={() => handleDeleteProposal(activeProposal.id)}
                  className="px-3 py-1.5 text-xs text-red-500 border border-red-200 rounded hover:bg-red-50">
                  Delete
                </button>
              </div>
            </div>

            {/* Review banner */}
            {activeProposal.review_notes && activeProposal.status === 'revision_requested' && (
              <div className="mx-4 mt-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 print:hidden">
                <span className="font-medium">Revision requested</span>
                {activeProposal.reviewed_by_name && ` by ${activeProposal.reviewed_by_name}`}
                {activeProposal.reviewed_at && ` on ${new Date(activeProposal.reviewed_at).toLocaleDateString()}`}
                : {activeProposal.review_notes}
              </div>
            )}
            {activeProposal.status === 'approved' && (
              <div className="mx-4 mt-3 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-700 print:hidden">
                <span className="font-medium">Approved</span>
                {activeProposal.reviewed_by_name && ` by ${activeProposal.reviewed_by_name}`}
                {activeProposal.reviewed_at && ` on ${new Date(activeProposal.reviewed_at).toLocaleDateString()}`}
                {activeProposal.review_notes && `: ${activeProposal.review_notes}`}
              </div>
            )}

            <div className="flex-1 flex overflow-hidden">
              {/* Editor */}
              <div className="flex-1 flex flex-col p-4 overflow-hidden">
                <input type="text" value={proposalTitle}
                  onChange={e => setProposalTitle(e.target.value)}
                  placeholder="Proposal title"
                  disabled={activeProposal.status === 'in_review' || activeProposal.status === 'approved' || activeProposal.status === 'submitted'}
                  className="w-full px-3 py-2 text-lg font-semibold border border-gray-200 rounded mb-3 focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-50 print:border-none print:text-2xl print:font-bold print:mb-6" />
                <textarea
                  value={proposalContent}
                  onChange={e => setProposalContent(e.target.value)}
                  disabled={activeProposal.status === 'in_review' || activeProposal.status === 'approved' || activeProposal.status === 'submitted'}
                  placeholder="Start writing your proposal..."
                  className="flex-1 w-full px-4 py-3 text-sm leading-relaxed border border-gray-200 rounded resize-none focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-50 print:border-none print:text-base print:leading-loose"
                />
              </div>

              {/* Versions sidebar */}
              {showVersions && (
                <div className="w-64 border-l border-gray-200 bg-white overflow-y-auto print:hidden">
                  <div className="p-3 border-b border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-700">Version History</h3>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {proposalVersions.map(v => (
                      <div key={v.id} className="p-3 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900">v{v.version}</span>
                          {v.version !== activeProposal.version && (
                            <button onClick={() => handleRestoreVersion(v.version)}
                              className="text-xs text-amber-600 hover:text-amber-800">Restore</button>
                          )}
                          {v.version === activeProposal.version && (
                            <span className="text-xs text-green-600">Current</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          {v.saved_at ? new Date(v.saved_at).toLocaleString() : ''}
                        </div>
                        {v.saved_by_name && <div className="text-xs text-gray-400">{v.saved_by_name}</div>}
                        {v.change_summary && <div className="text-xs text-gray-500 mt-1">{v.change_summary}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Print content (hidden on screen, shown when printing) */}
            <div className="hidden print:block p-8">
              <h1 className="text-2xl font-bold mb-4">{proposalTitle}</h1>
              <div className="whitespace-pre-wrap text-base leading-loose">{proposalContent}</div>
            </div>
          </div>
        )}

      </div>
    </AdminRouteGuard>
  );
};

export default AdminGrantManagement;
